'use client';

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';

type Level = 'senior' | 'lead' | 'executive';

type CalcState = {
  level: Level;
  salary: number;
  percent: number;
  count: number;
  conf: boolean;
  relocation: boolean;
  minFee: number;
  retainer: boolean;
};

type FormData = {
  name: string;
  phone: string;
  role: string;
  email: string;
  agreed: boolean;
};

const nf = new Intl.NumberFormat('ru-RU');
const cf = new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 });

function levelCoef(l: Level) {
  switch (l) {
    case 'executive': return 1.3;
    case 'lead': return 1.15;
    default: return 1.0;
  }
}

function extrasCoef(conf: boolean, relocation: boolean) {
  let c = 1.0;
  if (conf) c *= 1.15;
  if (relocation) c *= 1.10;
  return c;
}

function timeline(level: Level) {
  if (level === 'executive') return { first: '15–20 РД', shortlist: '20–30 РД', close: '60–90 РД' };
  if (level === 'lead') return { first: '10–15 РД', shortlist: '15–25 РД', close: '45–75 РД' };
  return { first: '7–12 РД', shortlist: '12–20 РД', close: '35–60 РД' };
}

function Section({
  id, gray, className, children,
}: React.PropsWithChildren<{ id?: string; gray?: boolean; className?: string }>) {
  return (
    <section id={id} className={`${gray ? 'bg-slate-50' : ''} py-20 box-border ${className ?? ''}`}>
      <div className="mx-auto max-w-6xl px-6">{children}</div>
    </section>
  );
}

function Card({ className, children }: React.PropsWithChildren<{ className?: string }>) {
  return <div className={`rounded-2xl bg-white p-6 shadow-[0_6px_18px_rgba(0,0,0,0.06)] ${className ?? ''}`}>{children}</div>;
}

function KPI({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/30 bg-white/10 px-4 py-2 text-white backdrop-blur h-[72px]">
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/60 text-base">{icon}</div>
      <div>
        <div className="text-xl font-extrabold leading-none">{value}</div>
        <div className="text-sm opacity-90 leading-none">{label}</div>
      </div>
    </div>
  );
}

function LabeledInputNumber(props: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  step?: number;
}) {
  const { label, value, onChange, min, step = 1 } = props;
  return (
    <label className="block">
      <span className="text-sm text-slate-600">{label}</span>
      <input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        min={min}
        step={step}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-600 focus:outline-none"
      />
    </label>
  );
}

function LabeledSelect<T extends string>(props: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  const { label, value, onChange, options } = props;
  return (
    <label className="block">
      <span className="text-sm text-slate-600">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-600 focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-extrabold text-[#003B9B]">{value}</div>
      {hint && <div className="text-xs text-slate-500">{hint}</div>}
    </Card>
  );
}

function getStatusColorClass(status: string) {
  if (status.startsWith('❌')) return 'text-red-600 font-medium';
  if (status.startsWith('✅')) return 'text-green-600 font-medium';
  return 'text-slate-500';
};

export default function RareTalentPage() {
  const [state, setState] = useState<CalcState>(() => {
    if (typeof window === 'undefined') {
      return {
        level: 'lead',
        salary: 220_000,
        percent: 22,
        count: 1,
        conf: true,
        relocation: false,
        minFee: 250_000,
        retainer: true,
      };
    }
    try {
      const raw = localStorage.getItem('rare_calc');
      return raw ? JSON.parse(raw) as CalcState : {
        level: 'lead',
        salary: 220_000,
        percent: 22,
        count: 1,
        conf: true,
        relocation: false,
        minFee: 250_000,
        retainer: true,
      };
    } catch {
      return {
        level: 'lead',
        salary: 220_000,
        percent: 22,
        count: 1,
        conf: true,
        relocation: false,
        minFee: 250_000,
        retainer: true,
      };
    }
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('rare_calc', JSON.stringify(state));
    }
  }, [state]);

  const annual = state.salary * 12;
  const baseFee = Math.max(state.minFee, annual * (state.percent / 100) * levelCoef(state.level));
  const finalFeeOne = Math.round(baseFee * extrasCoef(state.conf, state.relocation));
  const total = finalFeeOne * state.count;

  const t = timeline(state.level);

  const retainerSplit = {
    a: Math.round(finalFeeOne * 0.30),
    b: Math.round(finalFeeOne * 0.30),
    c: Math.round(finalFeeOne * 0.40),
  };

  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    role: '',
    email: '',
    agreed: false,
  });
  const [formStatus, setFormStatus] = useState('');

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setFormStatus('Отправка...');

    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim() || !formData.role.trim()) {
      setFormStatus('❌ Пожалуйста, заполните все поля (Имя, Телефон, Роль, Email).');
      return;
    }

    if (!formData.agreed) {
      setFormStatus('❌ Пожалуйста, дайте согласие на обработку персональных данных.');
      return;
    }

    const apiUrl = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/rare-talent-requests`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            ...formData,
            formName: 'Подбор редких специалистов'
          },
        }),
      });

      if (response.ok) {
        setFormStatus('✅ Ваша заявка успешно отправлена! Скоро мы с вами свяжемся.');
        setFormData({ name: '', phone: '', role: '', email: '', agreed: false });
      } else {
        const errorData = await response.json();
        setFormStatus(`❌ Произошла ошибка: ${errorData?.error?.message || 'неизвестная ошибка.'}`);
        console.error('Ошибка:', errorData);
      }
    } catch (error) {
      setFormStatus('❌ Ошибка сети. Пожалуйста, проверьте ваше подключение.');
      console.error('Ошибка:', error);
    }
  };

  return (
    <main className="bg-white text-slate-900">
      <section className="mt-20 bg-gradient-to-br from-[#00214F] to-[#003B9B] text-white pt-24 pb-20 box-border">
        <div className="mx-auto max-w-6xl pl-1 pr-6">
          <h1 className="text-4xl font-black md:text-5xl">Подбор редких специалистов</h1>
          <p className="mt-3 max-w-3xl text-base md:text-lg opacity-95 leading-tight">
            Подбор персонала на сложные и дефицитные должности: картирование рынка, целевой поиск, конфиденциальные переговоры, поддержка в предложении и адаптация сотрудников (30/60/90). Мы работаем на условиях гонорара за успешную работу.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button
              asChild
              className="w-full sm:w-auto inline-block bg-[#FF7C22] text-white py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base hover:text-white hover:bg-[#FF7C22]"
            >
              <a href="#calc" className="flex items-center justify-center h-full">Оценить бюджет</a>
            </Button>
            <Button
              asChild
              className="w-full sm:w-auto inline-block bg-white text-[#003B9B] border-2 border-[#003B9B] py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base hover:text-[#003B9B] hover:bg-white"
            >
              <a href="#mapping" className="flex items-center justify-center h-full">Market mapping</a>
            </Button>
          </div>
          <div className="mt-7 rounded-2xl border border-white/30 bg-white/10 p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KPI icon={<span>🎯</span>} value="90%+" label="релевантности отобранных кандидатов" />
              <KPI icon={<span>📜</span>} value="90–180 дн." label="гарантия замены" />
              <KPI icon={<span>🗺</span>} value="до 200+" label="карта компаний/кандидатов" />
              <KPI icon={<span>📈</span>} value="еженед." label="статус-репорты и риски" />
            </div>
          </div>
        </div>
      </section>

      <Section gray>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Card>
            <h3 className="text-xl font-extrabold">Когда это нужно</h3>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
              <li>Мало кандидатов на рынке, критичные компетенции</li>
              <li>Нужно «переманить» лидеров из целевых компаний</li>
              <li>Высокая конфиденциальность поиска</li>
              <li>Новое направление/филиал, собрать ядро команды</li>
            </ul>
          </Card>
          <Card>
            <h3 className="text-xl font-extrabold">Что делаем</h3>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
              <li id="mapping"><b>Исследование рынка</b>: карты компаний, ролей и ответвлений</li>
              <li>Прямой поиск, целевое освещение, аргументация EVP</li>
              <li>Интервью по компетенциям, кейсы и рекомендации</li>
              <li>Управление предложением и поддержка результатов</li>
            </ul>
          </Card>
          <Card>
            <h3 className="text-xl font-extrabold">Комплаенс</h3>
            <p className="mt-3 text-base text-black">
              Соблюдаем ТК РФ и 152-ФЗ. Конфиденциальность кандидатов и заказчика закрепляем в договоре, соглашение о неразглашении (NDA). Недискриминационный поиск.
            </p>
          </Card>
        </div>
      </Section>

      <Section id="calc">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card>
            <h3 className="text-xl font-extrabold">Калькулятор подбора</h3>
            <p className="mt-1 text-xs text-slate-500">
              Черновая оценка комиссии. Расчёт от годового дохода (12× оклад) с коэффициентом сложности роли.
            </p>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <LabeledSelect<Level>
                label="Уровень роли"
                value={state.level}
                onChange={(v) => setState({ ...state, level: v })}
                options={[
                  { value: 'senior', label: 'Senior / Редкий специалист' },
                  { value: 'lead', label: 'Lead / Руководитель направления' },
                  { value: 'executive', label: 'Executive / Топ-менеджер' },
                ]}
              />
              <LabeledInputNumber
                label="Оклад, ₽/мес (gross)"
                value={state.salary}
                onChange={(v) => setState({ ...state, salary: v })}
                min={0}
              />
              <LabeledInputNumber
                label="Кол-во закрытий"
                value={state.count}
                onChange={(v) => setState({ ...state, count: v })}
                min={1}
              />
              <LabeledInputNumber
                label="Минимальный фикс, ₽ (за 1 закрытие)"
                value={state.minFee}
                onChange={(v) => setState({ ...state, minFee: v })}
                min={0}
              />
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-4">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={state.conf}
                  onChange={(e) => setState({ ...state, conf: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300"
                />
                Конфиденциальный поиск (+15%)
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={state.relocation}
                  onChange={(e) => setState({ ...state, relocation: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300"
                />
                Релокация/переезд (+10%)
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={state.retainer}
                  onChange={(e) => setState({ ...state, retainer: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300"
                />
                Retainer (30/30/40)
              </label>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <StatCard label="Годовой доход (база)" value={cf.format(annual)} />
              <StatCard label="Комиссия за 1 закрытие" value={cf.format(finalFeeOne)} hint="с учётом сложности/доп. условий" />
              <StatCard label="Итого за все позиции" value={cf.format(total)} />
              {state.retainer ? (
                <Card>
                  <div className="text-xs text-slate-500">Retainer (30/30/40)</div>
                  <div className="mt-1 text-sm text-slate-700">
                    Этап 1 (старт): <b>{cf.format(retainerSplit.a)}</b><br />
                    Этап 2 (shortlist): <b>{cf.format(retainerSplit.b)}</b><br />
                    Этап 3 (оффер/выход): <b>{cf.format(retainerSplit.c)}</b>
                  </div>
                </Card>
              ) : (
                <StatCard label="Модель оплаты" value="Success fee" hint="оплата при выходе кандидата" />
              )}
            </div>
            <p className="mt-2 text-xs text-slate-500">Оценка носит ориентировочный характер; финальные условия фиксируем в договоре/SOW.</p>
          </Card>

          <Card>
            <h3 className="text-xl font-extrabold">Соглашение об уровне сервиса (SLA) и этапы</h3>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full border-collapse text-base">
                <thead>
                  <tr className="text-left text-black">
                    <th className="border-b border-slate-200 px-2 py-2 font-bold">Этап</th>
                    <th className="border-b border-slate-200 px-2 py-2 font-bold">Срок</th>
                    <th className="border-b border-slate-200 px-2 py-2 font-bold">Комментарий</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border-b border-slate-100 px-2 py-2">Первые лиды</td>
                    <td className="border-b border-slate-100 px-2 py-2">{t.first}</td>
                    <td className="border-b border-slate-100 px-2 py-2">после брифа и согласования профиля</td>
                  </tr>
                  <tr>
                    <td className="border-b border-slate-100 px-2 py-2">Shortlist</td>
                    <td className="border-b border-slate-100 px-2 py-2">{t.shortlist}</td>
                    <td className="border-b border-slate-100 px-2 py-2">3–5 кандидатов</td>
                  </tr>
                  <tr>
                    <td className="border-b border-slate-100 px-2 py-2">Закрытие</td>
                    <td className="border-b border-slate-100 px-2 py-2">{t.close}</td>
                    <td className="border-b border-slate-100 px-2 py-2">в зависимости от рынка и оффера</td>
                  </tr>
                  <tr>
                    <td className="px-2 py-2">Отчётность</td>
                    <td className="px-2 py-2">еженедельно</td>
                    <td className="px-2 py-2">воронка, конверсии, риски, план на неделю</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-slate-500">Гарантия замены 90–180 дней — по уровню роли и пакету.</p>
          </Card>
        </div>
      </Section>

      <Section gray>
        <h2 className="mb-4 text-3xl font-black">Пакеты</h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            {
              name: 'Focus',
              items: ['1 роль', 'Исследование рынка 50–80 контактов', 'Прямой поиск и составление короткого списка', 'Гарантия 90 дней'],
              note: 'Для редких senior-специалистов',
            },
            {
              name: 'Lead',
              items: ['1–2 роли', 'Исследование 100–150', 'EVP-скрипты и тёплый поиск', 'Гарантия 120 дней'],
              note: 'Для руководителей направлений/тимлидов',
            },
            {
              name: 'Executive',
              items: ['Конфиденциально', 'Исследование 150–250 + реф-нетворк', 'Аванс 30/30/40', 'Гарантия 180 дней'],
              note: 'Для уровня C-1 / C-level',
            },
          ].map((p) => (
            <Card key={p.name}>
              <h3 className="text-xl font-black">{p.name}</h3>
              <ul className="mt-3 space-y-2 text-slate-700 list-disc pl-5">
                {p.items.map((i) => <li key={i}>{i}</li>)}
              </ul>
              <p className="mt-3 text-xs text-slate-500">{p.note}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section>
        <h2 className="mb-4 text-3xl font-black">Процесс</h2>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-6">
          {[
            { n: '1. Бриф', d: 'Профиль роли, целевые компании, оффер и EVP.' },
            { n: '2. Исследование', d: 'Карта рынка, список компаний и функций.' },
            { n: '3. Поиск', d: 'Точечные контакты, скрипты и воронка.' },
            { n: '4. Отбор', d: 'Интервью/кейсы, референсы, финальный лист отобранных кандидатов.' },
            { n: '5. Предложение', d: 'Переговоры, встречные предложения, принятие.' },
            { n: '6. Онбординг', d: 'Сопровождение 30/60/90 и гарантия.' },
          ].map((s) => (
            <Card key={s.n}>
              <h3 className="text-lg font-extrabold whitespace-nowrap">{s.n}</h3>
              <p className="mt-1 text-base text-black">{s.d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section gray>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card>
            <h3 className="text-xl font-extrabold">Кейс</h3>
            <p className="mt-2 text-base text-black"><b>Задача:</b> Найти менеджера по исследованиям и разработкам с опытом запуска продукта.</p>
            <p className="mt-1 text-base text-black"><b>Сделано:</b> 160 профилей, 38 целевых контактов, 5 отобрано; предложение принято на 52 рабочих дня.</p>
            <p className="mt-1 text-base text-black"><b>Результат:</b> Запуск + 90-дневная поддержка, устранение рисков встречного предложения.</p>
          </Card>
          <Card>
            <h3 className="text-xl font-extrabold">FAQ</h3>
            <details className="group mt-2 border-t border-slate-200 py-3" open>
              <summary className="cursor-pointer text-base font-bold">Какая модель оплаты?</summary>
              <p className="mt-1 text-base text-black">Аванс (30/30/40) или гонорар за успех; существует минимальная комиссия за закрытие сделки.</p>
            </details>
            <details className="group border-t border-slate-200 py-3">
              <summary className="cursor-pointer text-base font-bold">Делаете конфиденциальный поиск?</summary>
              <p className="mt-1 text-base text-black">Да, с соглашением о неразглашении и анонимностью бренда до финального этапа.</p>
            </details>
            <details className="group border-t border-slate-200 py-3">
              <summary className="cursor-pointer text-base font-bold">Как проверяете кандидатов?</summary>
              <p className="mt-1 text-base text-black">Интервью по компетенциям, кейсы, рекомендации; проверка биографических данных в соответствии с законодательством доступна по запросу.</p>
            </details>
          </Card>
        </div>
      </Section>

      <Section id="cta">
        <Card>
          <h3 className="text-xl font-extrabold">Запросить поиск редкого специалиста</h3>
          <form className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs text-slate-500">Имя</span>
              <input
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                placeholder="Как к вам обращаться"
                required
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-slate-500">Телефон</span>
              <input
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                placeholder="+7 () ___--__-__"
                required
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="mb-1 block text-xs text-slate-500">Роль</span>
              <input
                name="role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                placeholder="Например: Head of R&D, Москва, 350–450 тыс."
                required
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="mb-1 block text-xs text-slate-500">Email</span>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                placeholder="for@example.com"
                required
              />
            </label>

            <label className="flex items-start text-xs sm:text-sm cursor-pointer text-slate-600 sm:col-span-2">
              <input
                type="checkbox"
                checked={formData.agreed}
                onChange={(e) => setFormData({ ...formData, agreed: e.target.checked })}
                className="mt-1 mr-2 h-4 w-4 rounded border-slate-300 text-[#003B9B] focus:ring-[#003B9B]"
              />
              <span>
                Я даю <a href="/privacy" className="text-[#003B9B] underline hover:text-[#FF7C22]">согласие на обработку персональных данных</a> и соглашаюсь с <a href="/privacy" className="text-[#003B9B] underline hover:text-[#FF7C22]">Политикой конфиденциальности</a>.
              </span>
            </label>

            <div className="sm:col-span-2">
              <button
                type="button"
                onClick={handleSubmit}
                className="inline-block rounded-xl bg-[#FF7C22] px-6 py-3 text-sm font-bold text-white shadow hover:opacity-95"
              >
                Отправить
              </button>
              {formStatus && <p className={`mt-2 text-xs ${getStatusColorClass(formStatus)}`}>{formStatus}</p>}
            </div>
          </form>
          <p className="mt-2 text-xs text-slate-500">По запросу направим пример market mapping и шаблон отчётности.</p>
        </Card>
      </Section>
    </main>
  );
}